import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import JobApply, { JobApplyContainer }
from '@fuww/library/src/JobApply';
import FormSubmitConfirmation
from '@fuww/library/src/FormSubmitConfirmation';
import messages from '../../../lib/messages';
import throw404 from '../../../lib/throw404';
import Head from '../../Head';
import LinkAndAnchor from '../../LinkAndAnchor';
import ApplyForm from './ApplyForm';
import HomeLink from '../../HomeLink';
import getJobDetails from '../../../lib/getJobDetails';
import { Router } from '../../../routes';
import { useSiteContext } from '../../SiteContext';
import getDescription from '../JobDetail/getDescription';
import getJobsRoute from '../../../lib/getJobsRoute';
import REDIRECT_TIMEOUT from '../../../lib/redirectTimeout';
import { getRouterState } from '../../../lib/savedRouterState';

const breadcrumbLinks = ({
  title, locale, id, slug,
}) => [
  <HomeLink />,
  <LinkAndAnchor route={`jobs.${locale}`}>
    Fashion Jobs
  </LinkAndAnchor>,
  <LinkAndAnchor route={`job.${locale}`} params={{ id, slug }}>
    {title}
  </LinkAndAnchor>,
];

const JobApplication = ({
  data: {
    job,
  },
}) => {
  const intl = useIntl();
  const { locale } = useSiteContext();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [lastJobsQuery, setLastJobsQuery] = useState({});

  useEffect(() => {
    setLastJobsQuery({ ...getRouterState('jobs')?.query });
  }, []);

  useEffect(() => {
    if (uploadSuccess) {
      setTimeout(() => {
        Router.pushRoute(
          getJobsRoute(lastJobsQuery, locale), { ...lastJobsQuery },
        );
      }, REDIRECT_TIMEOUT);
    }
  }, [uploadSuccess, locale, lastJobsQuery]);

  if (!job || !job.active) {
    return throw404();
  }

  const {
    title, _id, id, slug,
  } = job;

  const applyTitle = (
    <>
      {`${intl.formatMessage(messages['applicantForm.title'])}:`}
      <br />
      {title}
    </>
  );

  const applyForm = (
    <ApplyForm
      jobId={id}
      setUploadSuccess={setUploadSuccess}
    />
  );

  if (uploadSuccess) {
    return (
      <>
        <Head
          title={title}
          description={getDescription(job, intl)}
          subjectType="Job"
          subjectId={_id}
        />
        <JobApplyContainer
          title={title}
          breadcrumbLinks={breadcrumbLinks({
            title, locale, id, slug,
          })}
        >
          <FormSubmitConfirmation
            successHeadline={intl.formatMessage(messages.thankYou)}
            successMessage={
              intl.formatMessage(messages['applicantForm.applicationSent'])
            }
            redirectMessage={
              intl.formatMessage(messages['applicantForm.backToJobBoard'])
            }
          />
        </JobApplyContainer>
      </>
    );
  }

  return (
    <>
      <Head
        title={title}
        description={getDescription(job, intl)}
        subjectType="Job"
        subjectId={_id}
      />
      <JobApplyContainer
        title={applyTitle}
        applyForm={applyForm}
        backButton={(
          <LinkAndAnchor
            route={getJobsRoute(lastJobsQuery, locale)}
            params={{ ...lastJobsQuery }}
            useRouter
          >
            {intl.formatMessage(messages['jobs.returnToList'])}
          </LinkAndAnchor>
        )}
        breadcrumbLinks={breadcrumbLinks({
          title, locale, id, slug,
        })}
      >
        <JobApply
          jobDetails={getJobDetails(job, intl, locale)}
          privacyDisclaimer={
            intl.formatMessage(messages['applicantForm.disclaimer'])
          }
        >
          {applyForm}
        </JobApply>
      </JobApplyContainer>
    </>
  );
};

JobApplication.propTypes = {
  data: PropTypes.shape({
    job: PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.number,
      slug: PropTypes.string,
      title: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      company: PropTypes.shape({
        name: PropTypes.string,
      }),
      expiresAt: PropTypes.string,
      publishedAt: PropTypes.string,
      categories: PropTypes.arrayOf(
        PropTypes.string,
      ),
      active: PropTypes.bool,
    }),
  }).isRequired,
};

export default JobApplication;
